import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Brain, Server, Cloud, Check, AlertCircle, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import apiService from '../../services/apiService';

const SettingsSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const SectionIcon = styled.div`
  width: 20px;
  height: 20px;
  color: ${({ theme }) => theme.colors.primary[500]};
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.dark.text};
  margin: 0;
`;

const SectionDescription = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  margin: ${({ theme }) => theme.spacing.sm} 0 0 0;
  line-height: 1.5;
`;

const ProviderSection = styled.div`
  background: ${({ theme }) => theme.colors.dark.elevated};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const ProviderOption = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.md};
  border: 2px solid ${({ $selected, theme }) => 
    $selected ? theme.colors.primary[500] : theme.colors.dark.border
  };
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ $selected, theme }) => 
    $selected ? `${theme.colors.primary[500]}10` : theme.colors.dark.surface
  };
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary[400]};
    background: ${({ $selected, theme }) => 
      $selected ? `${theme.colors.primary[500]}15` : `${theme.colors.primary[500]}05`
    };
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ProviderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const ProviderIcon = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.primary[500]}20;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.primary[400]};
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const ProviderDetails = styled.div`
  flex: 1;
`;

const ProviderName = styled.h3`
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.dark.text};
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
`;

const ProviderDescription = styled.p`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  margin: 0;
  line-height: 1.4;
`;

const CheckIcon = styled.div`
  width: 24px;
  height: 24px;
  color: ${({ theme }) => theme.colors.primary[500]};
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const GroqConfigSection = styled(motion.div)`
  background: ${({ theme }) => theme.colors.dark.surface};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.dark.text};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const Select = styled.select`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.dark.elevated};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.dark.text};
  font-size: 0.875rem;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary[500]};
    background: ${({ theme }) => theme.colors.dark.surface};
  }
`;

const InputGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const Input = styled.input`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.dark.elevated};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.dark.text};
  font-size: 0.875rem;
  font-family: 'Courier New', monospace;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary[500]};
    background: ${({ theme }) => theme.colors.dark.surface};
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.dark.textSecondary};
  }
`;

const Button = styled(motion.button)`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: ${({ $variant, theme }) => 
    $variant === 'primary' ? theme.colors.primary[500] : theme.colors.dark.elevated
  };
  border: 1px solid ${({ $variant, theme }) => 
    $variant === 'primary' ? theme.colors.primary[500] : theme.colors.dark.border
  };
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ $variant, theme }) => 
    $variant === 'primary' ? 'white' : theme.colors.dark.text
  };
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  transition: all ${({ theme }) => theme.transitions.fast};
  white-space: nowrap;
  
  &:hover {
    background: ${({ $variant, theme }) => 
      $variant === 'primary' ? theme.colors.primary[600] : theme.colors.dark.surface
    };
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  background: ${({ $status, theme }) => {
    if ($status === 'connected') return `${theme.colors.status?.online || '#10b981'}20`;
    if ($status === 'error') return `${theme.colors.status?.offline || '#ef4444'}20`;
    return `${theme.colors.gray[600]}20`;
  }};
  border: 1px solid ${({ $status, theme }) => {
    if ($status === 'connected') return theme.colors.status?.online || '#10b981';
    if ($status === 'error') return theme.colors.status?.offline || '#ef4444';
    return theme.colors.gray[600];
  }};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: ${({ $status, theme }) => {
    if ($status === 'connected') return theme.colors.status?.online || '#10b981';
    if ($status === 'error') return theme.colors.status?.offline || '#ef4444';
    return theme.colors.gray[400];
  }};
  font-size: 0.75rem;
  font-weight: 500;
  
  svg {
    width: 12px;
    height: 12px;
  }
`;

const InfoBox = styled.div`
  background: ${({ theme }) => theme.colors.primary[500]}10;
  border: 1px solid ${({ theme }) => theme.colors.primary[500]}30;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.md};
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  line-height: 1.5;
`;

const LLMProviderSettings = ({ onSettingsChange }) => {
  const [provider, setProvider] = useState('server_hosted');
  const [groqModel, setGroqModel] = useState('llama-3.3-70b-versatile');
  const [groqApiKey, setGroqApiKey] = useState('');
  const [groqKeyExists, setGroqKeyExists] = useState(false);
  const [groqKeyExpiresAt, setGroqKeyExpiresAt] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isTesting, setIsTesting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const groqModels = [
    { value: 'llama-3.3-70b-versatile', label: 'Llama 3.3 70B Versatile' },
    { value: 'openai/gpt-oss-20b', label: 'GPT OSS 20B' },
    { value: 'openai/gpt-oss-120b', label: 'GPT OSS 120B' },
    { value: 'moonshotai/kimi-k2-instruct-0905', label: 'Kimi K2 Instruct' },
    { value: 'qwen/qwen3-32b', label: 'Qwen3 32B' }
  ];

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('sarvik_token');
      
      if (!token) {
        console.error('No authentication token found');
        toast.error('Please login to access LLM settings');
        setIsLoading(false);
        return;
      }
      
      const settings = await apiService.getLLMSettings(token);
      
      setProvider(settings.provider);
      setGroqModel(settings.groq_model || 'llama-3.3-70b-versatile');
      setGroqKeyExists(settings.groq_key_exists);
      setGroqKeyExpiresAt(settings.groq_key_expires_at);
      
      if (settings.provider === 'groq_api' && settings.groq_key_exists) {
        setConnectionStatus('connected');
      }
    } catch (error) {
      console.error('Failed to load LLM settings:', error);
      toast.error('Failed to load LLM settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleModelChange = async (e) => {
    const newModel = e.target.value;
    setGroqModel(newModel);
    
    // If key exists, update the model in backend
    if (groqKeyExists) {
      try {
        const token = localStorage.getItem('sarvik_token');
        await apiService.updateGroqModel(token, newModel);
        toast.success(`Model updated to ${groqModels.find(m => m.value === newModel)?.label}`);
        onSettingsChange?.(true);
      } catch (error) {
        console.error('Failed to update model:', error);
        toast.error('Failed to update model');
      }
    }
  };

  const handleProviderChange = async (newProvider) => {
    if (newProvider === provider) return;
    
    // Check if switching to Groq without API key
    if (newProvider === 'groq_api' && !groqKeyExists) {
      toast.error('Please configure your Groq API key first');
      return;
    }
    
    try {
      const token = localStorage.getItem('sarvik_token');
      await apiService.updateLLMProvider(token, newProvider);
      
      setProvider(newProvider);
      toast.success(`Switched to ${newProvider === 'server_hosted' ? 'Server-Hosted' : 'Groq API'}`);
      onSettingsChange?.(true);
    } catch (error) {
      console.error('Failed to update provider:', error);
      toast.error(error.response?.data?.detail || 'Failed to update provider');
    }
  };

  const handleTestConnection = async () => {
    try {
      setIsTesting(true);
      setConnectionStatus(null);
      
      const token = localStorage.getItem('sarvik_token');
      await apiService.testGroqConnection(token);
      
      setConnectionStatus('connected');
      toast.success('Groq API connection successful!');
    } catch (error) {
      console.error('Connection test failed:', error);
      setConnectionStatus('error');
      toast.error(error.response?.data?.detail || 'Connection failed');
    } finally {
      setIsTesting(false);
    }
  };

  const handleSaveGroqKey = async () => {
    if (!groqApiKey.trim()) {
      toast.error('Please enter your Groq API key');
      return;
    }
    
    try {
      setIsSaving(true);
      
      const token = localStorage.getItem('sarvik_token');
      await apiService.saveGroqApiKey(token, groqApiKey, groqModel);
      
      setGroqKeyExists(true);
      setGroqApiKey(''); // Clear input for security
      setConnectionStatus('connected');
      toast.success('Groq API key saved successfully!');
      onSettingsChange?.(true);
      
      // Reload settings to get expiry date
      await loadSettings();
    } catch (error) {
      console.error('Failed to save API key:', error);
      toast.error(error.response?.data?.detail || 'Failed to save API key');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteGroqKey = async () => {
    if (!window.confirm('Are you sure you want to delete your Groq API key? This will switch you back to Server-Hosted mode.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('sarvik_token');
      await apiService.deleteGroqApiKey(token);
      
      setGroqKeyExists(false);
      setGroqApiKey('');
      setConnectionStatus(null);
      setProvider('server_hosted');
      toast.success('Groq API key deleted');
      onSettingsChange?.(true);
    } catch (error) {
      console.error('Failed to delete API key:', error);
      toast.error('Failed to delete API key');
    }
  };

  const openExternalLink = async (url) => {
    try {
      await window.electronAPI.openExternalLink(url);
    } catch (error) {
      console.error('Failed to open external link:', error);
      toast.error('Failed to open link in browser');
    }
  };

  if (isLoading) {
    return (
      <SettingsSection>
        <SectionHeader>
          <SectionIcon><Brain /></SectionIcon>
          <SectionTitle>LLM Provider</SectionTitle>
        </SectionHeader>
        <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
          <Loader style={{ animation: 'spin 1s linear infinite', margin: '0 auto' }} />
          <p style={{ marginTop: '1rem' }}>Loading settings...</p>
        </div>
      </SettingsSection>
    );
  }

  return (
    <SettingsSection>
      <SectionHeader>
        <SectionIcon><Brain /></SectionIcon>
        <div style={{ flex: 1 }}>
          <SectionTitle>LLM Provider</SectionTitle>
          <SectionDescription>
            Choose between Sarvik’s server-hosted model or an external cloud-based model (Groq API).
          </SectionDescription>
        </div>
      </SectionHeader>

      <ProviderSection>
        {/* Server Hosted Option */}
        <ProviderOption
          $selected={provider === 'server_hosted'}
          onClick={() => handleProviderChange('server_hosted')}
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
        >
          <ProviderInfo>
            <ProviderIcon>
              <Server />
            </ProviderIcon>
            <ProviderDetails>
              <ProviderName>Server-Hosted (Qwen3-4B)</ProviderName>
              <ProviderDescription>
                Runs on Sarvik servers • Free • Privacy-focused • Slightly slower performance
              </ProviderDescription>
            </ProviderDetails>
          </ProviderInfo>
          {provider === 'server_hosted' && (
            <CheckIcon>
              <Check />
            </CheckIcon>
          )}
        </ProviderOption>

        {/* Groq API Option */}
        <ProviderOption
          $selected={provider === 'groq_api'}
          onClick={() => handleProviderChange('groq_api')}
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
        >
          <ProviderInfo>
            <ProviderIcon>
              <Cloud />
            </ProviderIcon>
            <ProviderDetails>
              <ProviderName>Groq API (Cloud)</ProviderName>
              <ProviderDescription>
                Runs on Groq’s cloud service • Very fast responses • Requires API key
              </ProviderDescription>
            </ProviderDetails>
          </ProviderInfo>
          {provider === 'groq_api' && (
            <CheckIcon>
              <Check />
            </CheckIcon>
          )}
        </ProviderOption>
      </ProviderSection>

      {/* Groq Configuration (always shown for easy access) */}
      <GroqConfigSection
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
          <SectionHeader style={{ marginBottom: '1rem' }}>
            <SectionIcon><Cloud /></SectionIcon>
            <div style={{ flex: 1 }}>
              <SectionTitle style={{ fontSize: '1rem' }}>Groq API Configuration</SectionTitle>
              {!groqKeyExists && (
                <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: '0.25rem 0 0 0' }}>
                  Configure your API key below to enable cloud-based LLM
                </p>
              )}
            </div>
            {connectionStatus && (
              <StatusBadge $status={connectionStatus}>
                {connectionStatus === 'connected' ? <Check /> : <AlertCircle />}
                {connectionStatus === 'connected' ? 'Connected' : 'Connection Failed'}
              </StatusBadge>
            )}
          </SectionHeader>

          {/* Model Selection */}
          <FormGroup>
            <Label>Model</Label>
            <Select 
              value={groqModel} 
              onChange={handleModelChange}
            >
              {groqModels.map(model => (
                <option key={model.value} value={model.value}>
                  {model.label}
                </option>
              ))}
            </Select>
          </FormGroup>

          {/* API Key Input */}
          <FormGroup>
            <Label>
              API Key
              {groqKeyExists && (
                <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#10b981' }}>
                  ✓ Key saved
                  {groqKeyExpiresAt && (
                    <span style={{ marginLeft: '0.25rem', color: '#6b7280' }}>
                      (expires {new Date(groqKeyExpiresAt).toLocaleDateString()})
                    </span>
                  )}
                </span>
              )}
            </Label>
            <InputGroup>
              <Input
                type="password"
                value={groqApiKey}
                onChange={(e) => setGroqApiKey(e.target.value)}
                placeholder={groqKeyExists ? '••••••••••••••••' : 'gsk_...'}
                disabled={groqKeyExists}
              />
              {!groqKeyExists ? (
                <Button
                  $variant="primary"
                  onClick={handleSaveGroqKey}
                  disabled={isSaving || !groqApiKey.trim()}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {isSaving ? <Loader style={{ animation: 'spin 1s linear infinite' }} /> : 'Save'}
                </Button>
              ) : (
                <>
                  <Button
                    onClick={handleTestConnection}
                    disabled={isTesting}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {isTesting ? <Loader style={{ animation: 'spin 1s linear infinite' }} /> : 'Test'}
                  </Button>
                  <Button
                    onClick={handleDeleteGroqKey}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Delete
                  </Button>
                </>
              )}
            </InputGroup>
          </FormGroup>

          {/* Info Box */}
          <InfoBox>
            <strong>About Groq API:</strong>
            <ul style={{ margin: '0.5rem 0 0 1rem', paddingLeft: '0.5rem' }}>
              <li>Get your free API key at <a href="#" onClick={(e) => { e.preventDefault(); openExternalLink('https://console.groq.com'); }} style={{ color: '#60a5fa', cursor: 'pointer' }}>console.groq.com</a></li>
              <li>Keys are encrypted and stored for 30 days</li>
              <li>Your context and conversations are managed by SARVIK, not Groq</li>
              <li>Free tier: some models provides 14,400 requests/day (more than enough for personal use)</li>
            </ul>
          </InfoBox>
      </GroqConfigSection>
    </SettingsSection>
  );
};

export default LLMProviderSettings;
